import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { LoginUser } from '../../models/login-user.model';
import { ResponseToken } from '../../models/response-token.model';
import { RefreshToken } from '../../models/refresh-token.model';
import { PerfilUsuario } from '../../../shared/models';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { useLocalStorageSignal } from '@core/utils/use-local-storage-signal';
import { RealtimeService } from '../realtime/realtime.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  currentToken = useLocalStorageSignal<string | null>('accessToken1', null);

  /**
   * Guarda el perfil del usuario
   */
  private perfilUsuarioSubject = new BehaviorSubject<PerfilUsuario | null>(
    null,
  );
  perfilUsuario$ = this.perfilUsuarioSubject.asObservable();

  private realtimeService = inject(RealtimeService);

  API_URL = '';
  constructor(
    private readonly http: HttpClient,
    private router: Router,
  ) {
    this.API_URL = `${environment.API_URL_BASE}`;
  }

  /**
   * Carga el perfil decodificando el token JWT.
   * Es síncrono pero devuelve un Observable para ser compatible con APP_INITIALIZER.
   */
  cargarPerfilUsuario(): Observable<PerfilUsuario | null> {
    const token = this.getToken('accessToken');
    if (!token) {
      // Si no hay token, no hay nada que hacer.
      return of(null);
    }
    try {
      const perfilDecodificado = jwtDecode<PerfilUsuario>(token);
      // Si el token es válido y no ha expirado, actualizamos el estado.
      this.perfilUsuarioSubject.next(perfilDecodificado);
      return of(perfilDecodificado);
    } catch (error) {
      // El token está malformado o es inválido.
      console.error('Token encontrado pero inválido/malformado', error);
      this.clearState();
      return of(null);
    }
  }

  obtenerPerfilUsuario(): PerfilUsuario | null {
    return this.perfilUsuarioSubject.getValue();
  }

  /**
   * Mando los datos del usuario para obtener un token de sesión
   */
  public login(userCredentials: LoginUser): Observable<PerfilUsuario | null> {
    
    return this.http
      .post<ResponseToken>(
        this.API_URL + 'v1/auth/staff/login',
        userCredentials,
        {
          headers: {
            'Grips-Client-Platform': 'WEB',
          },
        },
      )
      .pipe(
        tap((respuesta) => {
          this.currentToken.set(respuesta.data.accessToken);
          this.realtimeService.connect(respuesta.data.accessToken);
          this.setToken('accessToken', respuesta.data.accessToken);
          this.setToken('refreshToken', respuesta.data.refreshToken);
        }),
        switchMap(() => this.cargarPerfilUsuario()),
        catchError((error) => {
          console.error('Error en el login:', error);
          // Nos aseguramos de que el estado esté limpio
          this.clearState();
          // Propagamos el error para que el componente de login pueda mostrar un mensaje
          throw error;
        }),
      );
  }

  public refreshToken(
    refreshToken: RefreshToken,
  ): Observable<{ ok: boolean; data: string }> {
    return this.http.post<{
      ok: boolean;
      data: string;
    }>(this.API_URL + 'v1/auth/staff/refresh', refreshToken);
  }

  /**
   * Funcion para cerrar sesión
   */
  public logout(): Observable<{ ok: boolean }> {
    return this.http
      .get<{ ok: boolean }>(this.API_URL + 'v1/auth/staff/logout')
      .pipe(
        tap((respuesta) => {
          if (respuesta.ok == true) {
            this.clearState();
          }
        }),
      );
  }

  clearState(): void {
    this.realtimeService.disconnect();
    this.deleteToken('accessToken');
    this.deleteToken('refreshToken');
    this.perfilUsuarioSubject.next(null);
    this.router.navigate(['auth/login']);
  }

  public setToken(name: string, value: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(name, value);
    } else {
      console.warn('localStorage is not available');
    }
  }

  public getToken(name: string): string | null {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem(name);
    } else {
      return null;
    }
  }

  public deleteToken(name: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(name);
    }
  }
  /**
   * Verifica si existe un tokcen de acceso
   */
  isAuthenticatedUser(name: string) {
    return !!this.getToken(name);
  }
}
